import { useState, useEffect, useMemo, useCallback, useRef, Suspense } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import { useDispatch, useSelector } from "react-redux";
import { getUser } from "redux-store/auth/actions";
import { hasStoredAuthToken } from "utils/authToken";
import { loadCapabilities } from "redux-store/abac/actions";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

import SoftBox from "components/SoftBox";

import Sidenav from "pages/components/Sidenav";
import EnvironmentalPortalShell from "afms/components/EnvironmentalPortal/EnvironmentalPortalShell";
import LcpPortalShell from "lcp/components/LcpPortalShell";
import EnergyPortalShell from "energy/components/EnergyPortalShell";
import FixedOpsPortalShell from "fixedops/components/FixedOpsPortalShell";
import Configurator from "pages/components/Configurator";

import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

import routes from "routes";

import { useSoftUIController, setMiniSidenav, setOpenConfigurator } from "context";

// Images
import brand from "assets/images/logo-ct.png";
import ProtectedRoute from "ProtectedRoute";
import { useAppMode } from "context/AppModeContext";
import DefaultSystemPicker from "components/DefaultSystemPicker";
import QuickSignInOffer from "components/QuickSignIn/QuickSignInOffer";
import { pickSidebarVariant, selectUserDetails, SIDEBAR_VARIANTS } from "config/appMode";
import { setReportUserZone } from "lcp/utils/reportTime";

export default function App() {
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, direction, openConfigurator, sidenavColor } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const { defaultRoute } = useAppMode();

  // The sidebar choice is driven by the org, not by the current page.
  // pickSidebarVariant is a pure helper on userDetails — see config/appMode.
  // selectUserDetails normalizes the two coexisting state.auth.user shapes
  // (post-login is the inner DTO; post-getUser is the outer wrapper).
  const reduxDispatch = useDispatch();
  const authUser = useSelector((state) => state.auth.user);
  const userDetails = selectUserDetails(authUser);
  const sidebarVariant = pickSidebarVariant(userDetails);

  // LCP reports print every time in the signed-in user's own time zone
  // (lcp/utils/reportTime). Kept in step here, above every route, so a zone
  // saved in My settings applies to the next document without a re-login.
  const userTimezone = authUser?.timezone || userDetails?.timezone || null;
  useEffect(() => {
    setReportUserZone(userTimezone);
  }, [userTimezone]);

  // Rehydrate state.auth.user on hard refresh. Without this, the Redux
  // store starts empty on every reload and pickSidebarVariant(null)
  // returns null → no sidebar renders → the page looks broken even
  // though the user is still authenticated (their authToken sits in
  // localStorage). DashboardNavbar already does this for flex2 routes,
  // but it isn't mounted on AFMS / LCP portal pages.
  //
  // Fires once on mount when the token exists but userDetails is null.
  // getUser is idempotent — getUserSuccess populates state.user from the
  // server. If it fails, getUserFailure clears authToken and the
  // catch-all route bounces to /login (correct behavior — token's bad).
  useEffect(() => {
    // Only bootstrap the session when we actually hold a token. A bare
    // localStorage.getItem("authToken") truthy check would also pass on a
    // leftover placeholder ("", "undefined", "null"), firing an
    // unauthenticated POST /auth/user-info that 401s and toasts on /login.
    if (!userDetails && hasStoredAuthToken()) {
      reduxDispatch(getUser());
    }
  }, [userDetails, reduxDispatch]);

  // ── ABAC capability bootstrap (Phase 4 §4.1) ───────────────────────────────
  // Once the user is known, load their capability map exactly once. This
  // populates state.abac.capabilities (actions_by_resource_type + flat actions)
  // and the permission cache that <CanPerform>/usePermittedActions/useAbacPermission
  // read, so the UI can hide/disable actions the server would deny. The ref guards
  // against StrictMode/HMR double-invocation; it resets when the user clears
  // (logout) so a subsequent login / org-switch re-fetches.
  const abacCapabilities = useSelector((state) => state.abac?.capabilities);
  const abacCapabilitiesLoading = useSelector((state) => state.abac?.loading?.capabilities);
  const capsRequestedRef = useRef(false);
  useEffect(() => {
    if (!userDetails) {
      capsRequestedRef.current = false;
      return;
    }
    if (!abacCapabilities && !abacCapabilitiesLoading && !capsRequestedRef.current) {
      capsRequestedRef.current = true;
      reduxDispatch(loadCapabilities());
    }
  }, [userDetails, abacCapabilities, abacCapabilitiesLoading, reduxDispatch]);

  // The system-name label on either sidebar (e.g. "Environmental" /
  // "Fixed Operations") opens the system picker. Picker state lives here
  // so the click handler on the sidebar and the picker modal share the
  // same source of truth. The Flex2 logo itself navigates home — it's
  // not the picker trigger.
  const [pickerForceOpen, setPickerForceOpen] = useState(false);
  const handleSwitchSystem = useCallback(() => setPickerForceOpen(true), []);
  const handlePickerClose = useCallback(() => setPickerForceOpen(false), []);

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });

  const configsButton = (
    <SoftBox
      display="none"
      justifyContent="center"
      alignItems="center"
      width="3.5rem"
      height="3.5rem"
      // bgColor="white"
      // shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      {/* <Icon fontSize="default" color="inherit">
        settings
      </Icon> */}
    </SoftBox>
  );

  // console.log(direction)

  const routesBlock = (
    <Suspense fallback={<div style={{ padding: "2rem", textAlign: "center" }}>Loading...</div>}>
      <Routes>
        {getRoutes(routes)}
        {/* Catch-all ALWAYS goes to /login. localStorage.authToken
            can lie (stale tokens from a logged-out session) — trusting
            it sent unauth users straight into /fixedops/control-center
            via defaultRoute. Login.js's own already-authed effect
            fires the proper post-login redirect when the token is
            still valid, so logged-in users hit /login briefly and
            bounce to their home; logged-out users see the login form. */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Suspense>
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/*
        DefaultSystemPicker — two-mode component.
          • Self-gates against needsDefaultSystemPick on first-login (no
            preferredAppMode saved yet AND multiple systems available).
          • Or opens on demand via forceOpen when the user clicks the Flex2
            logo on either sidebar.
        See src/components/DefaultSystemPicker for the decision tree.
      */}
      <DefaultSystemPicker forceOpen={pickerForceOpen} onClose={handlePickerClose} />
      {/* After a full sign-in, offer a quick sign-in PIN for this device (once). */}
      <QuickSignInOffer />

      {/*
        ONE sidebar decision, made once, based purely on the org. The page
        the user is on does not get a vote. pickSidebarVariant returns:
          - null            → unauthenticated (login page) — render routes only
          - "main"          → org 1 backoffice OR non-Environmental org
          - "environmentalPortal" → Environmental org (green-leaf shell)
        EXCEPT: auth-flow paths (login, password reset, etc.) ALWAYS
        render bare — even when redux still has stale userDetails from
        the previous session a logged-out user shouldn't see a sidebar
        on /login.
      */}
      {(/^\/(login|forgot-password|reset-password|signup|verify|two-factor|lcpinspectionapp|afms\/driver|auth\/callback)(\/|$)/.test(pathname)) ? (
        // The LCP Inspection PWA (/lcpinspectionapp/*) and the AFMS
        // driver's QR pickup page (/afms/driver/*) are chrome-less
        // mobile shell: no sidebar, no top nav, no configurator. It
        // renders as bare routes so operators on a phone see nothing
        // but the PWA's own header and body.
        routesBlock
      ) : sidebarVariant === SIDEBAR_VARIANTS.MAIN ? (
        <>
          <Sidenav
            color={sidenavColor}
            brand={brand}
            brandName="Flex"
            routes={routes}
            onSwitchSystem={handleSwitchSystem}
          />
          <Configurator />
          {configsButton}
          {routesBlock}
        </>
      ) : sidebarVariant === SIDEBAR_VARIANTS.ENVIRONMENTAL_PORTAL ? (
        <EnvironmentalPortalShell onSwitchSystem={handleSwitchSystem}>
          {routesBlock}
        </EnvironmentalPortalShell>
      ) : sidebarVariant === SIDEBAR_VARIANTS.LCP_PORTAL ? (
        <LcpPortalShell onSwitchSystem={handleSwitchSystem}>
          {routesBlock}
        </LcpPortalShell>
      ) : sidebarVariant === SIDEBAR_VARIANTS.ENERGY_PORTAL ? (
        <EnergyPortalShell onSwitchSystem={handleSwitchSystem}>
          {routesBlock}
        </EnergyPortalShell>
      ) : sidebarVariant === SIDEBAR_VARIANTS.FIXED_OPS_PORTAL ? (
        <FixedOpsPortalShell onSwitchSystem={handleSwitchSystem}>
          {routesBlock}
        </FixedOpsPortalShell>
      ) : (
        routesBlock
      )}
    </ThemeProvider>
  );
}
